import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { getFamilyGame, getFamilyGameHistory, getFamilyGameSummary, runFamilyGameAdminAction, submitFamilyGameVote, type FamilyGameHistoryRound, type FamilyGameState } from "../lib/familyGames";
import type { HouseholdMember } from "../lib/familyLists";
import { db } from "../lib/firebase";
import { AvatarPortrait } from "./AvatarEditor";

type HouseHuntStatus = { status?: "scheduled" | "hiding" | "found" | "expired"; winnerName?: string };

type ScoreGridRow = {
  uid: string;
  rank: number;
  name: string;
  faceoff: number;
  streak: number;
  hunt: number;
  famBook: number;
  total: number;
};

export function FamilyGamesWidget({ householdId, userUid, members }: { householdId: string; userUid: string; members: HouseholdMember[] }) {
  const [game, setGame] = useState<FamilyGameState | null>(null);
  const [period, setPeriod] = useState("Weekly");
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("Loading today’s game…");
  const [menuOpen, setMenuOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [historyView, setHistoryView] = useState<"questions" | "awards">("questions");
  const [history, setHistory] = useState<FamilyGameHistoryRound[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [houseHunt, setHouseHunt] = useState<HouseHuntStatus | null>(null);
  const currentMember = members.find((member) => member.uid === userUid);
  const isAdmin = currentMember?.role === "owner" || currentMember?.role === "admin";

  async function refresh() {
    try {
      const next = await getFamilyGame(householdId);
      setGame(next); setStatus("");
      void getFamilyGameSummary(householdId)
        .then((summary) => setGame((current) => current ? { ...current, ...summary } : { ...next, ...summary }))
        .catch(() => setStatus("Today’s Face-Off is ready. Scores are taking a little longer."));
    }
    catch { setStatus("The family game couldn’t load. Try again in a moment."); }
  }
  useEffect(() => { void refresh(); }, [householdId]);
  useEffect(() => onSnapshot(
    doc(db, "households", householdId, "gameState", "homeHunt"),
    (snapshot) => setHouseHunt(snapshot.exists() ? snapshot.data() as HouseHuntStatus : null),
    () => setHouseHunt(null)
  ), [householdId]);

  async function openHistory(view: "questions" | "awards" = "questions") {
    setHistoryView(view);
    setHistoryOpen(true);
    if (view === "awards" || history || historyLoading) return;
    setHistoryLoading(true);
    try { setHistory(await getFamilyGameHistory(householdId)); }
    catch { setStatus("Past questions couldn’t load. Try again in a moment."); }
    finally { setHistoryLoading(false); }
  }

  async function vote(choice: string) {
    setBusy(true); setStatus("Saving your answer…");
    try {
      const faceoff = await submitFamilyGameVote(householdId, choice);
      setGame((current) => current ? { ...current, ...faceoff } : faceoff);
      setStatus("Answer locked in.");
      void getFamilyGameSummary(householdId)
        .then((summary) => setGame((current) => current ? { ...current, ...summary } : current))
        .catch(() => undefined);
    }
    catch (error) { setStatus(error instanceof Error ? error.message.replace(/^FirebaseError:\s*/i, "") : "That answer didn’t save."); }
    finally { setBusy(false); }
  }

  async function admin(action: string, details: Record<string, unknown> = {}) {
    setBusy(true);
    try {
      const faceoff = await runFamilyGameAdminAction(householdId, action, details);
      setGame((current) => current ? { ...current, ...faceoff } : faceoff);
      setStatus("Game updated."); setMenuOpen(false);
    }
    catch (error) { setStatus(error instanceof Error ? error.message.replace(/^FirebaseError:\s*/i, "") : "That game control didn’t work."); }
    finally { setBusy(false); }
  }

  const round = game?.round;
  const isSpotlight = round?.type === "know-family" && round.spotlightUid === userUid;
  const periodScores = game?.scoresByPeriod?.[period] || game?.scores || [];
  const scoreRows = useMemo<ScoreGridRow[]>(() => periodScores.map((person, index) => ({
    uid: person.uid,
    rank: index + 1,
    name: person.name,
    faceoff: person.faceoff + person.trivia,
    streak: person.streak,
    hunt: person.hunt,
    famBook: person.famBook,
    total: person.total
  })), [periodScores]);
  const houseHuntLabel = houseHunt?.status === "found"
    ? `House found by ${houseHunt.winnerName || "a family member"}`
    : houseHunt?.status === "hiding"
      ? "The house is hiding now!"
      : "House Hunt appears daily, 10 AM–6 PM";
  const recapDateLabel = game?.weeklyRecap
    ? `${new Date(`${game.weeklyRecap.weekStart}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}–${new Date(`${game.weeklyRecap.weekEnd}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
    : "";
  const voters = members.filter((member) => game?.votedUids?.includes(member.uid));
  const memberForChoice = (choice: { id: string; label: string }) => members.find((member) =>
    member.uid === choice.id
    || member.firstName.toLocaleLowerCase() === choice.label.toLocaleLowerCase()
    || `${member.firstName} ${member.lastName || ""}`.trim().toLocaleLowerCase() === choice.label.toLocaleLowerCase()
  );
  return (
    <section className="family-games-shell" aria-label="Family games">
      <header className="family-games-status-header">
        <div><p className="leader-label">Family Games</p><h2>Face-Off &amp; Awards</h2></div>
        {game?.yesterdayResult && <button type="button" className="game-yesterday-teaser" onClick={() => void openHistory()}>
          <span><b>Yesterday’s answer:</b> {game.yesterdayResult.answer || "Result unavailable"}{game.yesterdayResult.answer && <> · {game.yesterdayResult.correctCount} correct</>}</span>
          <strong>See everyone’s guesses →</strong>
        </button>}
        <div className="family-games-header-actions">
          <p className={`family-games-status-pill ${houseHunt?.status === "found" ? "found" : houseHunt?.status === "hiding" ? "hiding" : "scheduled"}`} role="status"><span aria-hidden="true" />{houseHuntLabel}</p>
        </div>
      </header>
      <div className="games-widget-grid">
        <article className="home-card standard-widget game-widget-card faceoff-card" data-widget-id="family-face-off">
          <div className="home-card-heading game-widget-heading">
            <div><p className="leader-label">{round?.eyebrow || "Family Face-Off"}</p><h3>{round?.type === "family-vote" ? "Who is most likely to…" : "Today’s challenge"}</h3></div>
            <div className="game-heading-actions"><span className="live-pill">{round?.status === "revealed" ? "Revealed" : "Live"}</span>{isAdmin && <button type="button" className="game-menu-button" aria-label="Open Face-Off controls" onClick={() => setMenuOpen((open) => !open)}>•••</button>}</div>
          </div>
          {menuOpen && <div className="game-admin-menu">
            <strong>Face-Off controls</strong>
            {!game && <span className="game-menu-status">Game controls are waiting for the live game service.</span>}
            <div className="game-control-group">
              <span>Answer rule</span>
              <div className="game-answer-rule" role="group" aria-label="Who may choose the same answer">
                <button type="button" className={(round?.allowDuplicates ?? true) ? "active" : ""} disabled={!game || busy} onClick={() => admin("answer-rule", { allowDuplicates: true })}><b>Multiple</b><small>Same answer allowed</small></button>
                <button type="button" className={game && !round?.allowDuplicates ? "active" : ""} disabled={!game || busy} onClick={() => admin("answer-rule", { allowDuplicates: false })}><b>One each</b><small>One person per answer</small></button>
              </div>
            </div>
            {voters.length > 0 && <div className="game-control-group game-clear-votes"><span>Clear an answer</span>{voters.map((member) => <button type="button" disabled={busy} key={member.uid} onClick={() => admin("clear-vote", { uid: member.uid })}>Clear {member.firstName}’s vote</button>)}{voters.length > 1 && <button type="button" className="clear-all" disabled={busy} onClick={() => admin("clear-all")}>Clear all votes</button>}</div>}
            <div className="game-control-group game-skip-control"><span>Question</span><button type="button" disabled={!game || busy} onClick={() => admin("skip")}>Skip this question <b>→</b></button></div>
          </div>}
          <p className="faceoff-question">{round?.question || "Loading today’s question…"}</p>
          {isSpotlight && <div className="game-featured-message">
            <strong>Your family is guessing your answer today.</strong>
            <span>If at least 2 of 3 know you, you earn a point too.</span>
          </div>}
          <div className={`player-picks game-choice-grid ${round?.choices.length === 2 ? "two-choice" : ""} ${isSpotlight ? "featured-waiting" : ""}`}>
            {round?.choices.map((choice) => {
              const selected = game?.ownVote === choice.id;
              const winner = round.status === "revealed" && round.winningChoices?.includes(choice.id);
              const choiceMember = round.type === "family-vote" ? memberForChoice(choice) : undefined;
              return <button type="button" key={choice.id} className={`${selected ? "selected " : ""}${winner ? "winner" : ""}`} disabled={busy || Boolean(game?.ownVote) || round.status !== "open" || isSpotlight} onClick={() => vote(choice.id)}>
                <b className={choiceMember?.avatar ? "game-choice-avatar has-avatar" : "game-choice-avatar"}>{choiceMember?.avatar ? <AvatarPortrait avatar={choiceMember.avatar} /> : choice.label.slice(0, 1)}</b>
                <span>{choice.label}</span>
              </button>;
            })}
          </div>
          <div className="game-widget-footer"><span>{game ? `${game.votedCount} of ${game.memberCount} answered` : status}</span><strong>{round?.resultLabel || (isSpotlight ? "Let’s see who knows you" : game?.ownVote ? "Answer locked" : "Make your pick")}</strong></div>
          <p className="game-live-status" role="status">{status}</p>
        </article>

        <article className="home-card standard-widget game-widget-card standings-card" data-widget-id="scores-streaks">
          <div className="home-card-heading game-widget-heading"><div><p className="leader-label">The Budhus</p><h3>Scores & Streaks</h3></div><button type="button" className="fambucks-rules-open" onClick={() => setRulesOpen(true)}><span aria-hidden="true">★</span><b>How to earn<br />FamBucks</b></button></div>
          <div className="period-tabs game-period-tabs">{["Weekly", "Monthly", "YTD", "All Time"].map((value) => <button type="button" key={value} className={period === value ? "active" : ""} onClick={() => setPeriod(value)}>{value}</button>)}</div>
          {scoreRows.length > 0 && <div className="family-score-table" role="table" aria-label={`${period} family scores`}>
            <div className="family-score-table-row family-score-table-header" role="row">
              <span role="columnheader">Player</span>
              <span role="columnheader">Total</span>
              <span role="columnheader">Face-Off</span>
              <span role="columnheader">{period === "Weekly" ? "Streak" : "Best Streak"}</span>
              <span role="columnheader">House Hunt</span>
              <span role="columnheader" aria-label="FamBook">FB</span>
            </div>
            {scoreRows.map((person) => <div className="family-score-table-row" role="row" key={person.uid}>
              <span className="family-score-player" role="cell"><small>#{person.rank}</small><strong title={person.name}>{person.name}</strong></span>
              <strong className="family-score-total" role="cell">{person.total}</strong>
              <span className="family-score-number" role="cell" aria-label={`${person.faceoff} Face-Off points`}>🥊 {person.faceoff}</span>
              <span className="family-score-number" role="cell" aria-label={period === "Weekly" ? `${Math.max(0, person.streak)} streak points` : `${Math.max(0, person.streak)} day best streak`}>{person.streak > 0 ? "🔥" : "—"} {Math.max(0, person.streak)}</span>
              <span className="family-score-number" role="cell" aria-label={`${person.hunt} House Hunt points`}>🏠 {person.hunt}</span>
              <span className="family-score-number" role="cell" aria-label={`${person.famBook} FamBook participation points`}>📖 {person.famBook}</span>
            </div>)}
          </div>}
          {!periodScores.length && <div className="game-score-empty">Scores appear after the family’s first round.</div>}
        </article>
      </div>
      {rulesOpen && <div className="game-history-backdrop" role="presentation" onMouseDown={(event) => {
        if (event.target === event.currentTarget) setRulesOpen(false);
      }}>
        <section className="fambucks-rules-modal" role="dialog" aria-modal="true" aria-labelledby="fambucks-rules-title">
          <header>
            <div><p className="leader-label">The weekly score</p><h3 id="fambucks-rules-title">How to earn FamBucks</h3></div>
            <button type="button" aria-label="Close FamBucks rules" onClick={() => setRulesOpen(false)}>×</button>
          </header>
          <p className="fambucks-rules-equation">Your weekly total = all four ways to earn</p>
          <div className="fambucks-rules-grid">
            <article><b aria-hidden="true">🥊</b><span><strong>Face-Off</strong><small><em>+1</em> correct · <em>0</em> wrong · <em>−1</em> skipped</small></span></article>
            <article><b aria-hidden="true">🔥</b><span><strong>Longest streak</strong><small><em>+1</em> for each day in your longest streak. Never negative.</small></span></article>
            <article><b aria-hidden="true">🏠</b><span><strong>House Hunt</strong><small>Find the hidden house to earn the daily point.</small></span></article>
            <article><b aria-hidden="true">📖</b><span><strong>FamBook</strong><small><em>+1</em> per meaningful contribution, up to 3 a day and 10 a week.</small></span></article>
          </div>
          <aside className="fambucks-spotlight-rule">
            <b aria-hidden="true">⭐</b>
            <span><strong>When the FamBook question is about you</strong><small>You sit out with no penalty. If at least two family members know your answer, you earn <em>+1</em>.</small></span>
          </aside>
        </section>
      </div>}
      {historyOpen && <div className="game-history-backdrop" role="presentation" onMouseDown={(event) => {
        if (event.target === event.currentTarget) setHistoryOpen(false);
      }}>
        <section className="game-history-modal" role="dialog" aria-modal="true" aria-labelledby="game-history-title">
          <header>
            <div><p className="leader-label">Face-Off history</p><h3 id="game-history-title">{historyView === "questions" ? "Past questions & results" : "Last week’s awards"}</h3></div>
            <button type="button" aria-label="Close past questions" onClick={() => setHistoryOpen(false)}>×</button>
          </header>
          <div className="period-tabs game-period-tabs game-history-tabs" role="tablist" aria-label="Family Games history">
            <button type="button" role="tab" aria-selected={historyView === "questions"} className={historyView === "questions" ? "active" : ""} onClick={() => void openHistory("questions")}>Past Answers</button>
            <button type="button" role="tab" aria-selected={historyView === "awards"} className={historyView === "awards" ? "active" : ""} onClick={() => void openHistory("awards")}>Last Week’s Awards</button>
          </div>
          {historyView === "questions" ? <>
            <p className="game-history-explainer">Each question scores +1 for a correct answer, 0 for a wrong answer, and −1 when it is missed. Family-vote winners are the most popular choice; tied leaders both win.</p>
            <div className="game-history-list">
              {(history || []).map((item) => {
                const answerIds = item.correctChoice ? [item.correctChoice] : item.winningChoices;
                const answerLabels = answerIds.map((answer) => item.choices.find((choice) => choice.id === answer)?.label || answer);
                return <article key={item.roundId}>
                  <div className="game-history-round-heading">
                    <div><small>{new Date(`${item.playDate}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })} · {item.eyebrow}</small><strong>{item.question}</strong></div>
                    <b>{item.noConsensus ? "No consensus" : answerLabels.length ? `Answer: ${answerLabels.join(" or ")}` : item.resultLabel}</b>
                  </div>
                  {item.type === "know-family" && item.spotlightUid && <p className="game-history-featured">
                    {members.find((member) => member.uid === item.spotlightUid)?.firstName || "Featured family member"} earned +{item.spotlightPoints || 0} · {item.correctGuessCount || 0} of {Math.max(0, members.length - 1)} knew the answer
                  </p>}
                  <div className="game-history-picks">
                    {item.votes.map((pick) => <div key={pick.uid}>
                      <span><strong>{pick.memberName}</strong><small>{pick.missed ? "missed the question" : `picked ${pick.choiceLabel}`}</small></span>
                      <b className={pick.points > 0 ? "earned" : pick.points < 0 ? "missed" : ""}>{pick.points > 0 ? "+" : ""}{pick.points}</b>
                    </div>)}
                  </div>
                </article>;
              })}
              {historyLoading && <p className="game-history-empty">Loading past questions…</p>}
              {!historyLoading && !history?.length && <p className="game-history-empty">Completed questions will appear here as soon as a round is revealed.</p>}
            </div>
          </> : game?.weeklyRecap ? <>
            <p className="game-history-explainer">FamBucks = Face-Off score + longest visit streak + House Hunt points + FamBook participation. Each meaningful FamBook contribution earns +1, up to 3 per day and 10 per week.</p>
            <section className="game-weekly-recap game-weekly-recap-modal" aria-label={`Family Games awards for ${recapDateLabel}`}>
              <header>
                <div><p className="leader-label">Award week</p><strong>{recapDateLabel}</strong></div>
                <b>Final standings</b>
              </header>
              <div className="game-weekly-recap-list">
                {game.weeklyRecap.rows.map((person) => <div className="game-weekly-recap-row" key={person.uid}>
                  <span className="game-weekly-recap-player"><small>#{person.rank}</small><strong>{person.name}</strong></span>
                  <span className="game-weekly-recap-breakdown">
                    <small>🥊 Face-Off: {person.faceoffPoints}</small>
                    <small>🔥 Longest streak: +{person.streakPoints}</small>
                    <small>🏠 House Hunt: +{person.homeHuntPoints}</small>
                    <small>📖 FamBook: +{person.famBookPoints||0} ({person.famBookContributions||0} contributions)</small>
                  </span>
                  <strong className="game-weekly-recap-total">{person.famBucksAwarded >= 0 ? "+" : ""}{person.famBucksAwarded} FamBucks</strong>
                </div>)}
              </div>
            </section>
          </> : <p className="game-history-empty">Last week’s awards will appear after the Sunday recap.</p>}
        </section>
      </div>}
    </section>
  );
}
